import {HOMEPAGE_PATH} from 'nav/sidepanel/homepage/HomepageConstants';

/**
 * How to add your product area to Amplitude element autocapture
 *
 * 1. Append an object to {@link AMPLITUDE_PRODUCT_AREA_ALLOWLISTS} below.
 * 2. Set `id` to a stable slug (e.g. `'registry'`).
 * 3. List `urlRegexes` that match path-absolute URLs or full HTTP(S) URLs.
 *    Anchor at pathname start or absolute HTTP(S) origin so allowlisted suffixes
 *    (e.g. `/admin/predictions/...`, `/foo/models`) do not match.
 * 4. Tenant config `ANALYTICS_DISABLE_AUTOCAPTURE_PAGE_URL_ALLOWLIST`
 *    must stay off for these patterns to restrict element autocapture.
 *    When that flag is true, autocapture runs on all routes.
 * 5. Do not edit sanitization logic in `amplitudePrivacy.ts` for allowlist-only
 *    changes.
 */

export interface AmplitudeProductAreaAllowlist {
  id: string;
  urlRegexes: ReadonlyArray<RegExp>;
}

/** Wrap a path regex so Amplitude matches path-absolute and full HTTP(S) URLs. */
export function pageUrlRegex(path: RegExp): RegExp {
  return new RegExp(`^(?:https?:\\/\\/[^/?#]+)?\\/${path.source}(?:$|[?#])`);
}

/**
 * Product areas where Amplitude element autocapture is enabled, grouped so an area's
 * URLs stay together. Commented-out areas were added on behalf of their teams and are
 * not enabled: the owning team must vet its pages' data test attributes for leaked
 * customer data before the area is uncommented.
 */
export const AMPLITUDE_PRODUCT_AREA_ALLOWLISTS: ReadonlyArray<AmplitudeProductAreaAllowlist> = [
  // {
  //   id: 'insights',
  //   urlRegexes: [
  //     pageUrlRegex(/analytics/),
  //     pageUrlRegex(/analytics\/dashboards\/[^/?#]+/),
  //     pageUrlRegex(/analytics\/analyses\/[^/?#]+/),
  //   ],
  // },
  // {
  //   id: 'model-hub',
  //   urlRegexes: [
  //     pageUrlRegex(/model-hub/),
  //     pageUrlRegex(/model-hub\/models/),
  //     pageUrlRegex(/model-hub\/new-prediction/),
  //     pageUrlRegex(/model-hub\/predictions/),
  //     pageUrlRegex(/model-hub\/predictions\/[^/?#]+/),
  //     pageUrlRegex(/model-hub\/prediction-batches/),
  //     pageUrlRegex(/model-hub\/prediction-batches\/[^/?#]+/),
  //     pageUrlRegex(/models/),
  //     pageUrlRegex(/new-prediction/),
  //     pageUrlRegex(/predictions/),
  //     pageUrlRegex(/predictions\/[^/?#]+/),
  //     pageUrlRegex(/prediction-batches/),
  //     pageUrlRegex(/prediction-batches\/[^/?#]+/),
  //   ],
  // },
  // {
  //   id: 'ai-chat',
  //   urlRegexes: [
  //     pageUrlRegex(/chats\/new/),
  //     pageUrlRegex(/chats\/agent-foundries/),
  //     pageUrlRegex(/chats\/agent-factories/),
  //     pageUrlRegex(/chats\/[^/?#]+/),
  //     pageUrlRegex(/chats\/settings/),
  //     pageUrlRegex(/chats\/settings\/general/),
  //     pageUrlRegex(/chats\/settings\/skills/),
  //     pageUrlRegex(/chats\/settings\/skills\/[^/?#]+/),
  //     pageUrlRegex(/chats\/settings\/agents/),
  //     pageUrlRegex(/chats\/settings\/agents\/[^/?#]+/),
  //     pageUrlRegex(/chats\/settings\/connectors/),
  //     pageUrlRegex(/chats\/settings\/connectors\/[^/?#]+/),
  //     pageUrlRegex(/ai-connectors/),
  //     pageUrlRegex(/s\/[^/?#]+\/edit/),
  //     pageUrlRegex(/[^/?#]+\/f\/[^/?#]+\/[^/?#]+\/edit/),
  //     pageUrlRegex(/samples\/containers\/[^/?#]+/),
  //     pageUrlRegex(/samples\/boxes\/[^/?#]+/),
  //     pageUrlRegex(/samples\/plates\/[^/?#]+/),
  //     pageUrlRegex(/samples\/locations\/[^/?#]+/),
  //     pageUrlRegex(/samples\/locations\/[^/?#]+\/edit/),
  //     pageUrlRegex(/[^/?#]+\/template-collections\//),
  //     pageUrlRegex(/[^/?#]+\/template-collections\/[^/?#]+/),
  //   ],
  // },
  // {
  //   id: 'lab-automation',
  //   urlRegexes: [
  //     pageUrlRegex(/instrument-gateways/),
  //     pageUrlRegex(/connections\/listing/),
  //     pageUrlRegex(/connections\/[^/?#]+/),
  //     pageUrlRegex(/assay-schemas\/runs\//),
  //     pageUrlRegex(/assay-schemas\/runs\/create/),
  //     pageUrlRegex(/assay-schemas\/runs\/[^/?#]+/),
  //     pageUrlRegex(/assay-schemas\/runs\/json\/create/),
  //     pageUrlRegex(/assay-schemas\/runs\/json\/[^/?#]+/),
  //     pageUrlRegex(/[^/?#]+\/assay-schemas\/runs\//),
  //     pageUrlRegex(/[^/?#]+\/assay-schemas\/runs\/create/),
  //     pageUrlRegex(/[^/?#]+\/assay-schemas\/runs\/[^/?#]+/),
  //     pageUrlRegex(/[^/?#]+\/assay-schemas\/runs\/json\/create/),
  //     pageUrlRegex(/[^/?#]+\/assay-schemas\/runs\/json\/[^/?#]+/),
  //     pageUrlRegex(/assay-runs\/[^/?#]+/),
  //   ],
  // },
  {
    id: 'homepage',
    urlRegexes: [pageUrlRegex(new RegExp(HOMEPAGE_PATH.slice(1)))],
  },
];

/** Pages where Amplitude element autocapture is enabled. */
export const AMPLITUDE_PAGE_URL_ALLOWLIST: Array<RegExp> = AMPLITUDE_PRODUCT_AREA_ALLOWLISTS.flatMap(
  (area) => area.urlRegexes
);
